"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Truck, ShieldCheck, PhoneCall, ArrowRight, Droplets, Flame, Biohazard, Hammer, Building2, Sparkles, Info } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
interface ServiceArea {
  name: string;
  desc: string;
}
interface ServiceLink {
  title: string;
  to: string;
  iconName: string;
}
const serviceAreas: ServiceArea[] = [{
  name: "Fairfax County",
  desc: "Rapid emergency response for residential neighborhoods, town centers, and commercial hubs."
}, {
  name: "Arlington",
  desc: "Dedicated crews on call for urban residences, multi-family units, and corporate offices."
}, {
  name: "Alexandria",
  desc: "Careful mitigation tailored for historic properties, waterfront communities, and modern homes."
}, {
  name: "Loudoun County",
  desc: "Comprehensive disaster recovery across growing sub-divisions, estates, and tech corridors."
}, {
  name: "Prince William",
  desc: "Reliable mobile restoration dispatched promptly to family homes and regional facilities."
}];
const servicePages: ServiceLink[] = [{
  title: "Water Damage & Flood",
  to: "/WaterDamageFloodRestorationPage",
  iconName: "droplets"
}, {
  title: "Mold Remediation",
  to: "/MoldRemediationPage",
  iconName: "sparkles"
}, {
  title: "Fire, Smoke & Odor",
  to: "/FireSmokeOdorRestorationPage",
  iconName: "flame"
}, {
  title: "Sewage Cleanup",
  to: "/SewageCleanupPage",
  iconName: "droplets"
}, {
  title: "Biohazard Cleanup",
  to: "/BiohazardCleanupPage",
  iconName: "biohazard"
}, {
  title: "Asbestos & Lead",
  to: "/AsbestosLeadAbatementPage",
  iconName: "shield"
}, {
  title: "Reconstruction & Build",
  to: "/ReconstructionDesignBuildPage",
  iconName: "hammer"
}, {
  title: "Commercial & HOA",
  to: "/CommercialHOAPropertyRestorationPage",
  iconName: "building"
}];
function getServiceIcon(iconName: string) {
  switch (iconName) {
    case "droplets":
      return <Droplets className="size-4 text-primary" />;
    case "sparkles":
      return <Sparkles className="size-4 text-primary" />;
    case "flame":
      return <Flame className="size-4 text-primary" />;
    case "biohazard":
      return <Biohazard className="size-4 text-primary" />;
    case "hammer":
      return <Hammer className="size-4 text-primary" />;
    case "building":
      return <Building2 className="size-4 text-primary" />;
    case "shield":
    default:
      return <ShieldCheck className="size-4 text-primary" />;
  }
}
export default function Coverage() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut" as const
      }
    }
  };
  return <section id="northernvirginiaservicecoverage" data-nav="light" className="relative w-full bg-background py-16 md:py-24 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-12">
          <Badge variant="outline" className="mb-3 border-primary/30 text-primary font-medium">
            <Truck className="size-3.5 mr-1" />
            Regional Dispatch Network
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            Northern Virginia service coverage
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            TriServe provides 24/7 disaster restoration across Northern Virginia. Our local technicians
            dispatch straight to your address with extraction and remediation equipment ready to operate.
          </p>
        </div>

        {/* Dispatch & Facility Notice Alert */}
        <div className="mb-12">
          <Alert className="border-border bg-muted/60 text-foreground">
            <Info className="size-5 text-primary shrink-0" />
            <div className="ml-2">
              <AlertTitle className="text-base font-semibold text-foreground">
                Mobile Field Operations Notice
              </AlertTitle>
              <AlertDescription className="text-sm text-muted-foreground mt-1 leading-relaxed">
                TriServe dispatches mobile local crews throughout Northern Virginia and does not operate a
                walk-in customer branch office in Virginia. Directing crews straight from staging points allows
                us to arrive faster and perform all assessments, mitigation, and repairs directly on-site.
              </AlertDescription>
            </div>
          </Alert>
        </div>

        {/* Service Areas Grid */}
        <div className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold text-foreground">Counties and cities served</h3>
              <p className="text-sm text-muted-foreground">Direct service coverage in Northern Virginia.</p>
            </div>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {serviceAreas.map((area, i) => <motion.div key={area.name} variants={itemVariants} data-index={i}>
                <Card className="h-full border-border bg-card shadow-xs transition-colors hover:border-primary/40">
                  <CardContent className="p-5 flex flex-col justify-between h-full">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="size-4 text-primary shrink-0" />
                        <h4 className="font-semibold text-foreground text-base">{area.name}</h4>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{area.desc}</p>
                    </div>
                    <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs font-medium text-primary">
                      <span>24/7 Response</span>
                      <span className="size-2 rounded-full bg-primary" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </motion.div>
        </div>

        {/* Specialized Services Grid */}
        <div>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-4">
            <div>
              <h3 className="text-xl font-semibold text-foreground">Services available in Northern Virginia</h3>
              <p className="text-sm text-muted-foreground">
                All disciplines handled by direct technicians with complete insurance coordination.
              </p>
            </div>
            <Button asChild variant="outline" size="sm" className="w-fit">
              <Link to="/ContactRequestServicePage" className="inline-flex items-center gap-2">
                <PhoneCall className="size-3.5" />
                <span>Contact dispatch</span>
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            {servicePages.map((page, i) => <div key={page.to} data-index={i}>
                <Link to={page.to} className="group flex items-center justify-between p-3.5 rounded-lg border border-border bg-card hover:bg-muted/50 hover:border-primary/30 transition-colors">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="p-1.5 rounded-md bg-muted shrink-0 group-hover:bg-primary/10 transition-colors">
                      {getServiceIcon(page.iconName)}
                    </span>
                    <span className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
                      {page.title}
                    </span>
                  </div>
                  <ArrowRight className="size-4 text-muted-foreground group-hover:text-primary transition-transform group-hover:translate-x-0.5 shrink-0 ml-2" />
                </Link>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}